import { getAccessToken } from './authentication/supabaseService';
import { Platform } from '../types/Platforms';
import {
  SingleExtractionResponse,
  BatchExtractionResponse,
  BatchExtractionData,
  BatchSummary,
  OrderData
} from '../types/invoiceTypes';

export class InvoiceService {
  /**
   * Extract invoice data from a single file
   */
  static async extractSingleInvoice(
    platform: Platform,
    file: File,
    restaurantId?: string
  ): Promise<SingleExtractionResponse> {
    const formData = new FormData();
    formData.append('platform', platform);
    formData.append('file', file);

    if (restaurantId) {
      formData.append('restaurantId', restaurantId);
    }

    const accessToken = await getAccessToken();

    const response = await fetch('/api/invoices/testExtraction', {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to process invoice');
    }

    return data;
  }

  /**
   * Extract invoice data from multiple files (batch processing)
   * Platform is automatically set to ANYINVOICE on the backend
   */
  static async extractBatchInvoices(
    files: File[],
    restaurantId?: string
  ): Promise<BatchExtractionData> {
    if (files.length > 100) {
      throw new Error('Maximum 100 files allowed');
    }

    const formData = new FormData();

    if (restaurantId) {
      formData.append('restaurantId', restaurantId);
    }

    files.forEach(file => {
      formData.append('files', file);
    });

    const accessToken = await getAccessToken();

    const response = await fetch('/api/invoices/batchExtraction', {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const data: BatchExtractionResponse = await response.json();

    if (!response.ok) {
      // An error body (403 from the invoices_page guard, 400, 500) carries `error`/`details` and no
      // `results` at all — reading it unguarded threw a TypeError instead of showing the reason.
      const errorBody = data as unknown as { error?: string; details?: string };
      throw new Error(
        data.results?.find(r => r.error)?.error
        || errorBody.error
        || errorBody.details
        || 'Failed to process invoices'
      );
    }

    // Calculate summary statistics on the frontend
    const summary = this.calculateBatchSummary(data.results);

    return {
      ...data,
      summary
    };
  }

  /**
   * Calculate summary statistics from batch results
   */
  static calculateBatchSummary(results: BatchExtractionResponse['results']): BatchSummary {
    const allOrders = results.flatMap(r => r.orders);
    const totalOrders = allOrders.length;
    const totalCosts = results.reduce((sum, r) => sum + r.costs, 0);

    // Calculate totals from orders
    let totalWithTax = 0;
    let totalWithoutTax = 0;
    let totalVat = 0;

    allOrders.forEach(order => {
      const orderTotal = parseFloat(order.total?.toString() || '0') || 0;
      const orderVat = order.totalWithTaxes
        ? parseFloat(order.totalWithTaxes.toString()) - orderTotal
        : 0;

      totalWithTax += order.totalWithTaxes
        ? parseFloat(order.totalWithTaxes.toString())
        : orderTotal;
      totalVat += orderVat;
      totalWithoutTax += orderTotal;
    });

    return {
      totalOrders,
      totalWithTax: parseFloat(totalWithTax.toFixed(2)),
      totalWithoutTax: parseFloat(totalWithoutTax.toFixed(2)),
      totalVat: parseFloat(totalVat.toFixed(2)),
      totalCosts: parseFloat(totalCosts.toFixed(4))
    };
  }

  /**
   * Get all orders from batch results (flattened)
   */
  static getAllOrdersFromBatch(results: BatchExtractionResponse['results']): OrderData[] {
    return results.flatMap(r => r.orders);
  }
}
